import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Activity, Cpu, Clock, DollarSign, AlertOctagon } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="card-crisp p-5" data-testid={`stat-${testId}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-overline text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="mt-2 font-display text-3xl font-extrabold tracking-tight">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function Admin() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api
      .get("/admin/model-calls")
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err)
    return (
      <Layout>
        <div className="mx-auto max-w-md px-6 py-24 text-center">
          <AlertOctagon className="mx-auto h-8 w-8 text-destructive" />
          <p className="mt-3 text-sm text-muted-foreground">
            {t("admin.accessRequired")}
          </p>
        </div>
      </Layout>
    );

  if (!data)
    return (
      <Layout>
        <div className="mx-auto max-w-6xl px-6 py-12 text-sm text-muted-foreground">
          {t("admin.loadingObs")}
        </div>
      </Layout>
    );

  const { totals, summary, recent } = data;
  const costChart = summary.map((s) => ({
    name: s.model_id,
    cost: Number((s.estimated_cost * 1000).toFixed(3)),
    latency: s.avg_latency_ms,
  }));

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="mb-2 flex items-center gap-2">
          <Activity className="h-5 w-5 text-ikb" />
          <span className="text-xs font-semibold uppercase tracking-overline text-muted-foreground">
            {t("admin.overline")}
          </span>
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">
          {t("admin.title")}
        </h1>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Cpu} testId="total-calls" label={t("admin.totalCalls")} value={totals.calls} sub={t("admin.errors", { count: totals.errors })} />
          <Stat
            icon={Activity}
            testId="tokens"
            label={t("admin.tokens")}
            value={`${((totals.tokens_in + totals.tokens_out) / 1000).toFixed(1)}k`}
            sub={t("admin.tokensSub", { in: totals.tokens_in, out: totals.tokens_out })}
          />
          <Stat
            icon={DollarSign}
            testId="est-cost"
            label={t("admin.estCost")}
            value={`$${totals.estimated_cost.toFixed(4)}`}
          />
          <Stat
            icon={Clock}
            testId="models"
            label={t("admin.models")}
            value={summary.length}
            sub={t("admin.modelsSub")}
          />
        </div>

        {costChart.length > 0 && (
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="card-crisp p-6">
              <h3 className="mb-4 text-sm font-semibold">{t("admin.costChart")}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={costChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 6% 90%)" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                  <Tooltip />
                  <Bar dataKey="cost" fill="hsl(223 100% 33%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card-crisp p-6">
              <h3 className="mb-4 text-sm font-semibold">{t("admin.latencyChart")}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={costChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 6% 90%)" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                  <Tooltip />
                  <Bar dataKey="latency" fill="hsl(223 100% 45%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="mt-8 card-crisp overflow-hidden">
          <h3 className="border-b border-border p-5 text-sm font-semibold">{t("admin.breakdown")}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="model-breakdown-table">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {[
                    t("admin.th.model"),
                    t("admin.th.provider"),
                    t("admin.th.calls"),
                    t("admin.th.errors"),
                    t("admin.th.tokensIn"),
                    t("admin.th.tokensOut"),
                    t("admin.th.avgMs"),
                    t("admin.th.cost"),
                  ].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.map((s) => (
                  <tr key={s.model_id} className="border-t border-border font-mono">
                    <td className="px-4 py-2.5 font-semibold">{s.model_id}</td>
                    <td className="px-4 py-2.5">{s.provider}</td>
                    <td className="px-4 py-2.5">{s.calls}</td>
                    <td className="px-4 py-2.5">{s.errors}</td>
                    <td className="px-4 py-2.5">{s.tokens_in}</td>
                    <td className="px-4 py-2.5">{s.tokens_out}</td>
                    <td className="px-4 py-2.5">{s.avg_latency_ms}</td>
                    <td className="px-4 py-2.5">${s.estimated_cost.toFixed(5)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
